import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersistence from 'vuex-persist'

import sidebar from './module/sidebar'
import affiliates from './module/affiliates'

import router from '@/routes/router'

import bugsnagClient from '@/lib/bugsnag'
import constants from '@/constants'
import store from '@/store'

import moment from 'moment'

Vue.use(Vuex)

const initialState = {
  token: null,
  refreshToken: null,
  userStats: {},
  mainCourse: {},
  courses: [],
  courseData: [],
  bookmarkData: [],
  historyData: [],
  latestData: [],
  moreCourses: [],
  currentLesson: {},
  nextLessons: false,
  showDashboardSwitch: false,
  askOnboarding: Date.now() + 2 * 60 * 1000,
  onboardingAskedTimes: 0,
  showSuggestions: Date.now() + 10 * 60 * 1000,
  suggestionsShowedTimes: 0,
  isLiveStream: false,
  userDecks: []
}

export default new Vuex.Store({
  name: 'state',
  modules: {
    affiliates,
    sidebar
  },
  plugins: [new VuexPersistence().plugin],
  state: initialState,
  mutations: {
    SET_TOKEN(state, data) {
      state.token = data.token
      state.refreshToken = data.refreshToken
      Vue.axios.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${data.token}`
      Vue.axios.interceptors.response.use(
        (response) => {
          return response
        },
        (error) => {
          try {
            bugsnagClient.notify(error)
            if (error.response && error.response.status === 401) {
              store.dispatch('logout')
              router
                .push({ name: 'Log In' })
                .catch(() => window.location.reload())
            }
          } catch (e) {
            bugsnagClient.notify(e)
          }
          return Promise.reject(error)
        }
      )
    },
    APPLY_TOKEN(state) {
      Vue.axios.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${state.token}`
    },
    RESET_STATE(state) {
      Object.keys(state).forEach((key) => {
        state[key] = initialState[key]
      })
    },
    RESET_USER_INFO(state) {
      state.userInfo = initialState.userInfo
      state.token = ''
      state.refreshToken = ''
    },
    TOGGLE_BANNER(state, show) {
      state.userInfo.banner = show
    },
    TOGGLE_AUTO_MARK_STUDIED(state, status) {
      state.userInfo.autoMarkStudied = status
    },
    SET_USER_INFO(state, userInfo) {
      state.userInfo = { ...state.userInfo, ...userInfo }
    },
    SET_USER_STATS(state, userInfo) {
      state.userStats = { ...state.userStats, ...userInfo }
    },
    SET_USER_LEVEL(state, level) {
      state.userInfo.level = level
    },
    SET_USER_CHARSET(state, charSet) {
      state.userInfo.charSet = charSet
    },
    TOGGLE_PINYIN(state) {
      state.userInfo.pinyin = !state.userInfo.pinyin
    },
    SET_WEEKLY_GOAL(state, weeklyGoal) {
      state.userInfo.weeklyGoal = weeklyGoal
    },
    SET_CURRENT_LESSON(state, lessonData) {
      state.currentLesson = lessonData
    },
    TOGGLE_CURRENT_LESSON_STUDIED(state, studied) {
      state.currentLesson.studied = studied
    },
    TOGGLE_CURRENT_LESSON_SAVED(state, saved) {
      state.currentLesson.saved = saved
    },
    SET_MAIN_COURSE(state, mainCourse) {
      state.mainCourse = mainCourse
    },
    SET_USER_COURSES(state, lessonData) {
      state.courses = lessonData
    },
    SET_COURSE_DATA(state, lessonData) {
      state.courseData = lessonData
    },
    ADD_COURSE_DATA(state, lessonData) {
      state.courseData.push(lessonData)
    },
    SET_STUDIED_LESSONS(state, lessonData) {
      state.historyData = lessonData
    },
    SET_LATEST_LESSONS(state, lessonData) {
      state.latestData = lessonData
    },
    SET_BOOKMARKED_LESSONS(state, lessonData) {
      state.bookmarkData = lessonData
    },
    SET_MORE_COURSES(state, moreCourses) {
      state.moreCourses = moreCourses
    },
    REMOVE_DASHBOARD_INFO(state) {
      state.showDashboardSwitch = false
    },
    SCHEDULE_ONBOARDING_QUESTIONS(state, time) {
      state.onboardingAskedTimes += 1
      state.askOnboarding =
        time + (state.onboardingAskedTimes ^ (2 * 24 * 60 * 60 * 1000))
    },
    RESET_ONBOARDING_ASK_TIMES(state) {
      state.onboardingAskedTimes = 0
    },
    SCHEDULE_SUGGESTIONS(state, time = Date.now()) {
      state.suggestionsShowedTimes += 1
      state.showSuggestions =
        time + (state.suggestionsShowedTimes ^ (2 * 3 * 24 * 60 * 60 * 1000))
    },
    RESET_SUGGESTIONS_SHOWED_TIMES(state) {
      state.suggestionsShowedTimes = 0
    },
    SET_USER_DECKS(state, decks) {
      state.userDecks = decks
    },
    DELETE_USER_DECK(state, deckId) {
      state.userDecks = state.userDecks.filter((deck) => deck.id !== deckId)
    }
  },
  actions: {
    async logout(context) {
      await Vue.axios
        .get(process.env.VUE_APP_ROOT_API + '/api/v1/account/logout')
        .catch(() => {})
      context.commit('RESET_STATE')
      context.commit('RESET_USER_INFO')
    },
    async login(context, payload) {
      await Vue.axios
        .post(process.env.VUE_APP_ROOT_API + '/api/v1/entrance/login', payload)
        .then(({ data }) => {
          context.commit('SET_TOKEN', data)
          context.commit('APPLY_TOKEN')
        })
    },
    async signup(context, payload) {
      const prepareData = {
        emailAddress: payload.emailAddress,
        password: payload.password,
        clientType: process.env.VUE_APP_TYPE,
        clientKey: process.env.VUE_APP_SECRET
      }
      await Vue.axios
        .post(
          process.env.VUE_APP_ROOT_API + '/api/v1/entrance/signup-app',
          prepareData
        )
        .then(({ data }) => {
          context.commit('SET_TOKEN', data)
          context.commit('APPLY_TOKEN')
        })
    },
    async googleSSO(context, accessToken) {
      return await Vue.axios
        .post(`${process.env.VUE_APP_ROOT_API}/api/v1/sso/google`, {
          accessToken
        })
        .then(({ data }) => {
          context.commit('SET_TOKEN', data)
          return data
        })
        .catch((err) => {
          return Promise.reject()
        })
    },
    async facebookSSO(context, accessToken) {
      return await Vue.axios
        .post(`${process.env.VUE_APP_ROOT_API}/api/v1/sso/facebook`, {
          accessToken
        })
        .then(({ data }) => {
          context.commit('SET_TOKEN', data)
          return data
        })
        .catch((err) => {
          return Promise.reject()
        })
    },
    getUserInfo(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(process.env.VUE_APP_ROOT_API + '/api/v1/dashboard/get-info')
          .then(({ data }) => {
            context.commit('SET_USER_INFO', data)
            resolve()
          })
          .catch(async (error) => {
            await context.dispatch('logout')
            await router
              .push({ name: 'Log In' })
              .catch(() => window.location.reload())
            reject(error)
          })
      })
    },
    getUserStats(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(process.env.VUE_APP_ROOT_API + '/api/v1/dashboard/get-stats')
          .then(({ data }) => {
            context.commit('SET_USER_STATS', data)
            resolve()
          })
          .catch((error) => reject(error))
      })
    },
    changeLevel(context, level) {
      return new Promise((resolve, reject) => {
        context.commit('SET_USER_LEVEL', level)
        Vue.axios
          .put(process.env.VUE_APP_ROOT_API + '/api/v1/onboarding/level', {
            level: level
          })
          .then(async () => {
            await Promise.all([
              context.dispatch('getUserInfo'),
              context.dispatch('getUserStats')
            ])
            resolve()
          })
          .catch((error) => reject(error))
      })
    },
    changeCharSet(context, charSet) {
      return new Promise((resolve, reject) => {
        context.commit('SET_USER_CHARSET', charSet)
        Vue.axios
          .put(
            process.env.VUE_APP_ROOT_API + '/api/v1/account/settings/update',
            {
              type: 'charSet',
              value: charSet
            }
          )
          .then(() => resolve())
          .catch((error) => reject(error))
      })
    },
    togglePinyin(context) {
      return new Promise((resolve, reject) => {
        context.commit('TOGGLE_PINYIN')
        Vue.axios
          .put(
            process.env.VUE_APP_ROOT_API + '/api/v1/account/settings/update',
            {
              type: 'pinyin',
              value: context.state.userInfo.pinyin
            }
          )
          .then(() => resolve())
          .catch((error) => reject(error))
      })
    },
    setWeeklyGoal(context, weeklyGoal) {
      return new Promise((resolve, reject) => {
        context.commit('SET_WEEKLY_GOAL', weeklyGoal)
        Vue.axios
          .put(
            process.env.VUE_APP_ROOT_API + '/api/v1/account/settings/update',
            {
              type: 'weeklyGoal',
              value: context.state.userInfo.weeklyGoal
            }
          )
          .then(() => resolve())
          .catch((error) => reject(error))
      })
    },
    currentLesson(context, lessonData) {
      return new Promise((resolve, reject) => {
        context.commit('SET_CURRENT_LESSON', lessonData)
        Vue.axios
          .put(
            process.env.VUE_APP_ROOT_API +
              '/api/v1/dashboard/onboarding/questions',
            {
              questionId: 'currentLesson',
              answer: lessonData.id
            }
          )
          .then(() => resolve())
          .catch(() => reject())
      })
    },
    async toggleCurrentLessonStudied(context, payload) {
      return new Promise((resolve, reject) => {
        context.commit('TOGGLE_CURRENT_LESSON_STUDIED', payload.studied)
        Vue.axios
          .post(
            process.env.VUE_APP_ROOT_API + '/api/v1/dashboard/toggle-studied',
            {
              lessonId: payload.lessonId,
              status: payload.studied
            }
          )
          .then(() => {
            resolve()
          })
          .catch(() => {
            reject()
          })
      })
    },
    toggleCurrentLessonSaved(context, payload) {
      return new Promise((resolve, reject) => {
        context.commit('TOGGLE_CURRENT_LESSON_SAVED', payload.saved)
        Vue.axios
          .post(
            process.env.VUE_APP_ROOT_API + '/api/v1/dashboard/toggle-saved',
            {
              lessonId: payload.lessonId,
              status: payload.saved
            }
          )
          .then(() => {
            resolve()
          })
          .catch(() => {
            reject()
          })
      })
    },
    getUserCourses(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(
            process.env.VUE_APP_ROOT_API + '/api/v1/dashboard/user-courses',
            {
              headers: {
                Authorization: `Bearer ${context.state.token}`
              }
            }
          )
          .then(({ data }) => {
            context.commit('SET_USER_COURSES', data)
            resolve()
          })
          .catch(() => {
            reject()
          })
      })
    },
    buildMainCourse(context) {
      return new Promise(async (resolve, reject) => {
        // IF NO COURSES - GET DUMMY COURSE
        if (context.courses.length === 0 && context.bookmarkData.length > 0) {
          context.commit('SET_MAIN_COURSE', {
            course: {
              course_title: 'Your Bookmarked Lessons',
              bookmarks: true
            },
            lessonData: context.bookmarkData
          })
          resolve()
        } else if (context.courses.length > 0) {
          let lessonData = await this.getCourseData(
            context.courses[0]['course'].id
          )
          if (lessonData.length > 0) {
            context.commit('SET_MAIN_COURSE', {
              course: context.courses[0]['course'],
              lessonData: lessonData
            })
            resolve()
          } else if (context.courses.length > 1) {
            lessonData = await this.getCourseData(
              context.courses[1]['course'].id
            )
            if (lessonData.length > 0) {
              context.commit('SET_MAIN_COURSE', {
                course: context.courses[1]['course'],
                lessonData: lessonData
              })
              resolve()
            }
          } else if (context.courses.length > 2) {
            lessonData = await this.getCourseData(
              context.courses[2]['course'].id
            )
            if (lessonData.length > 0) {
              context.commit('SET_MAIN_COURSE', {
                course: context.courses[2]['course'],
                lessonData: lessonData
              })
              resolve()
            }
          }
        }
      })
    },
    getMainCourse(context, courseId) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(
            process.env.VUE_APP_ROOT_API +
              `/api/v1/dashboard/course-lessons?courseId=${courseId}`
          )
          .then(({ data }) => {
            context.commit('SET_MAIN_COURSE', data)
            resolve()
          })
          .catch((error) => reject(error))
      })
    },
    getMoreCourses(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(process.env.VUE_APP_ROOT_API + `/api/v1/dashboard/more-courses`)
          .then(({ data }) => {
            context.commit('SET_MORE_COURSES', data)
            resolve()
          })
          .catch((error) => reject(error))
      })
    },
    updateLatestLessons(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(
            process.env.VUE_APP_ROOT_API +
              '/api/v1/dashboard/all-lessons?limit=20'
          )
          .then(({ data }) => {
            context.commit('SET_LATEST_LESSONS', data)
            resolve()
          })
          .catch((error) => reject(error))
      })
    },
    updateBookmarks(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(
            process.env.VUE_APP_ROOT_API +
              '/api/v1/dashboard/bookmarks?limit=30'
          )
          .then(({ data }) => {
            context.commit('SET_BOOKMARKED_LESSONS', data)
            resolve()
          })
          .catch((error) => reject(error))
      })
    },
    updateHistory(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(
            process.env.VUE_APP_ROOT_API + '/api/v1/dashboard/history?limit=30'
          )
          .then(({ data }) => {
            context.commit('SET_STUDIED_LESSONS', data)
            resolve()
          })
          .catch((error) => reject(error))
      })
    },
    dropThisCourse(context, payload) {
      if (payload.area === 'main') {
        if (context.state.courseData.length > 0) {
          context.commit('SET_MAIN_COURSE', context.state.courseData[0])
          if (context.state.courseData.length > 1) {
            context.commit(
              'SET_COURSE_DATA',
              context.state.courseData.slice(1, 5)
            )
          } else {
            context.commit('SET_COURSE_DATA', [])
          }
        } else if (this.bookmarkData) {
          context.commit('SET_MAIN_COURSE', {
            course: { course_title: 'Your Bookmarked Lessons' },
            lessonData: context.state.bookmarkData
          })
        } else {
          context.commit('SET_MAIN_COURSE', {})
        }
      } else if (payload.area === 'course') {
        context.commit(
          'SET_COURSE_DATA',
          context.state.courseData.filter((course) => {
            return course.course.id !== payload.courseId
          })
        )
      }
    },
    async getToken(context) {
      if (context.state.token && context.state.refreshToken) {
        const decodeToken = Vue.$jwt.decode(context.state.token)
        const expired = decodeToken.exp
        const now = moment().unix()
        if (expired - constants.beforeExpiredTime <= now) {
          this.dispatch('refreshToken')
        }
      } else {
        await Vue.axios
          .post(process.env.VUE_APP_ROOT_API + '/api/v1/token', {
            key: process.env.VUE_APP_SECRET,
            type: 'dashboard'
          })
          .then(({ data }) => context.commit('SET_TOKEN', data))
      }
    },
    async refreshToken(context) {
      await Vue.axios
        .post(process.env.VUE_APP_ROOT_API + '/api/v1/token/refresh', {
          clientKey: process.env.VUE_APP_SECRET,
          clientType: process.env.VUE_APP_TYPE,
          refreshToken: context.state.refreshToken
        })
        .then(({ data }) => {
          context.commit('SET_TOKEN', data)
        })
    },
    logEvent(context, payload) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .put(
            process.env.VUE_APP_ROOT_API + '/api/v1/dashboard/event',
            payload
          )
          .then(() => resolve())
          .catch(() => reject())
      })
    },
    toggleBanner(context, setting) {
      context.commit('TOGGLE_BANNER', setting)
      context.dispatch('logEvent', {
        action: `${setting ? 'show' : 'hide'}_upgrade_banner`,
        event_category: 'advertising',
        event_label: 'Upgrade Banner'
      })
    },
    toggleAutoMarkStudied(context, payload) {
      return new Promise((resolve, reject) => {
        context.commit('TOGGLE_AUTO_MARK_STUDIED', payload)
        Vue.axios
          .put(
            process.env.VUE_APP_ROOT_API + '/api/v1/account/settings/update',
            {
              type: 'autoMarkStudied',
              value: payload
            }
          )
          .then(() => resolve())
          .catch((error) => reject(error))
      })
    },
    async leaveLessonRating(context, payload) {
      await Vue.axios
        .put(process.env.VUE_APP_ROOT_API + '/api/v1/lessons/rating', payload)
        .catch()
    },
    async getUserDecks(context) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(process.env.VUE_APP_ROOT_API + '/api/v1/vocabulary/decks')
          .then(({ data }) => {
            context.commit('SET_USER_DECKS', data)
            resolve()
          })
          .catch((error) => reject(error))
      })
    }
  },
  getters: {
    userInfo: (state) => state.userInfo,
    newUser: (state) =>
      state.userInfo &&
      state.userInfo.createdAt &&
      new Date(Date.now() - 1000 * 60 * 60 * 24 * 14) <
        new Date(state.userInfo.createdAt),
    isAuthenticated: (state) => !!state.token && !!state.refreshToken,
    isUserAuthenticate: (state) => !!state.token && !!state.refreshToken,
    token: (state) => state.token,
    refreshToken: (state) => state.refreshToken
  }
})
